import * as Yup from "yup";
import Box from "@material-ui/core/Box";
import HelpIcon from "@material-ui/icons/Help";
import Tooltip from "@material-ui/core/Tooltip";
import React, { ForwardRefRenderFunction } from "react";
import { Field, Form, Formik } from "formik";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useDispatch } from "react-redux";

import CurrencyFormatInput from "../../../components/elements/CurrencyFormatInput";
import DatePicker from "../../../components/elements/DatePicker";
import FormFieldError from "../../../components/elements/FormFieldError";
import {
  calculateEstimatedBudget,
  endDateCheckErrorsOnChange,
  maxBidCheckErrorsOnChange,
  startDateCheckErrorsOnChange,
} from "../../../utils/yupValidationUtils";
import { parseCampaignDate, toCampaignDateISO } from "../../../utils/campaignDateUtils";
import { setCampaignBasicDetails } from "../../../redux/reducers/campaignToCreate";

const tooltips = {
  estimatedBudget: "The Estimated Budget is the Daily Budget multiplied by the number of days the campaign is running.",
};

export type FormInitialValues = {
  startDate: string | Date;
  endDate: string | Date;
  dailyBudget: number | string;
  estimatedBudget: number | string;
  maxBid: number | string;
};

type FormProps = {
  formInitialValues: FormInitialValues;
  handleSubmit: (values: FormInitialValues) => void;
};

type Ref = HTMLButtonElement;

// ── Styled components ─────────────────────────────────────────────────────────

const FormColumn = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 12,
});

const FieldGroup = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 4,
});

const FieldLabel = styled("div")(({ theme }: { theme: Theme }) => ({
  fontSize: 10,
  fontWeight: 700,
  color: theme.palette.brand.gray500,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
}));

const RequiredMark = styled("span")(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.coral,
}));

const SubGrid3 = styled("div")({
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  gap: 12,
});

const SubGrid2 = styled("div")({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
});

const FieldRow = styled("div")({
  display: "flex",
  alignItems: "flex-start",
  gap: 6,
});

const InputSkin = styled("div")(({ theme }: { theme: Theme }) => ({
  flex: 1,
  "& .MuiOutlinedInput-root": {
    background: theme.palette.brand.white,
    borderRadius: 8,
    fontSize: 13,
    color: theme.palette.brand.gray900,
    "& fieldset": {
      border: `1px solid ${theme.palette.brand.gray200}`,
      borderRadius: 8,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.brand.gray300,
    },
    "&.Mui-focused fieldset": {
      border: `1px solid ${theme.palette.brand.blueAccent}`,
    },
    "&.Mui-error fieldset": {
      border: `1px solid ${theme.palette.brand.errorRed}`,
    },
    "&.Mui-disabled": {
      background: theme.palette.brand.gray50,
      "& fieldset": {
        borderColor: theme.palette.brand.gray200,
      },
    },
  },
}));

const TooltipIcon = styled(HelpIcon)(({ theme }: { theme: Theme }) => ({
  fontSize: 18,
  color: theme.palette.brand.gray400,
  cursor: "pointer",
  marginTop: 14,
  flexShrink: 0,
}));

// ── Component ─────────────────────────────────────────────────────────────────

const BudgetAndTimelinePanelForm: ForwardRefRenderFunction<Ref, FormProps> = (
  { formInitialValues, handleSubmit },
  ref,
) => {
  const dispatch = useDispatch();
  const validationSchema = Yup.object().shape({
    dailyBudget: Yup.number().positive("Number must be positive").required("Required field."),
    maxBid: Yup.number()
      .positive("Number must be positive!")
      .max(100, "Max Bid can not exceed $100")
      .when("estimatedBudget", (estimatedBudget: number, schema: any) => {
        return schema.max(estimatedBudget, "Max Bid can not be greater than the Estimated Budget");
      })
      .required("Required field."),
    startDate: Yup.date().min(
      new Date(new Date().setDate(new Date().getDate() - 1)),
      "Please choose a future or current date!",
    ),
    endDate: Yup.date().when("startDate", (startDate: any, schema: any) => {
      if (startDate) {
        const dayAfter = new Date(startDate.getTime() + 86400000);
        return schema.min(dayAfter, "The end date must be after the start date");
      }
      return schema;
    }),
  });

  return (
    <Formik
      initialValues={formInitialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnBlur
      validateOnChange={false}
    >
      {({ values, setFieldValue, setFieldError }) => (
        <Form>
          <FormColumn>
            <SubGrid3>
              <FieldGroup>
                <FieldLabel>
                  Start Date <RequiredMark>*</RequiredMark>
                </FieldLabel>
                <InputSkin>
                  <Field
                    as={DatePicker}
                    value={parseCampaignDate(values.startDate)}
                    InputProps={{ name: "startDate" }}
                    onChange={(date: unknown) => {
                      const d = date as Date;
                      setFieldValue("startDate", toCampaignDateISO(d), false);
                      const budget = d ? calculateEstimatedBudget(d, values.endDate as Date, +values.dailyBudget) : 0;
                      setFieldValue("estimatedBudget", budget, false);
                      startDateCheckErrorsOnChange(d, setFieldError);
                      endDateCheckErrorsOnChange(d, values.endDate as Date, setFieldError);
                      maxBidCheckErrorsOnChange(values.maxBid as number, budget, setFieldError);
                      dispatch(setCampaignBasicDetails({ startDate: toCampaignDateISO(d), estimatedBudget: budget }));
                    }}
                  />
                </InputSkin>
                <Box style={{ minHeight: 15 }}>
                  <FormFieldError name="startDate" />
                </Box>
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>
                  End Date <RequiredMark>*</RequiredMark>
                </FieldLabel>
                <InputSkin>
                  <Field
                    as={DatePicker}
                    value={parseCampaignDate(values.endDate)}
                    InputProps={{ name: "endDate" }}
                    onChange={(date: unknown) => {
                      const d = date as Date;
                      setFieldValue("endDate", toCampaignDateISO(d), false);
                      const budget = d ? calculateEstimatedBudget(values.startDate as Date, d, +values.dailyBudget) : 0;
                      setFieldValue("estimatedBudget", budget, false);
                      endDateCheckErrorsOnChange(values.startDate as Date, d, setFieldError);
                      maxBidCheckErrorsOnChange(values.maxBid as number, budget, setFieldError);
                      dispatch(setCampaignBasicDetails({ endDate: toCampaignDateISO(d), estimatedBudget: budget }));
                    }}
                  />
                </InputSkin>
                <Box style={{ minHeight: 15 }}>
                  <FormFieldError name="endDate" />
                </Box>
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>
                  Max Bid <RequiredMark>*</RequiredMark>
                </FieldLabel>
                <InputSkin>
                  <Field
                    as={CurrencyFormatInput}
                    value={values.maxBid}
                    name="maxBid"
                    variant="outlined"
                    onChange={({ target }: any) => {
                      setFieldValue("maxBid", target.value, false);
                      maxBidCheckErrorsOnChange(
                        target.value as number,
                        values.estimatedBudget as number,
                        setFieldError,
                      );
                      dispatch(setCampaignBasicDetails({ maxBid: +target.value }));
                    }}
                  />
                </InputSkin>
                <Box style={{ minHeight: 15 }}>
                  <FormFieldError name="maxBid" />
                </Box>
              </FieldGroup>
            </SubGrid3>

            <SubGrid2>
              <FieldGroup>
                <FieldLabel>
                  Daily Budget <RequiredMark>*</RequiredMark>
                </FieldLabel>
                <InputSkin>
                  <Field
                    as={CurrencyFormatInput}
                    value={values.dailyBudget}
                    name="dailyBudget"
                    variant="outlined"
                    onChange={({ target }: { target: any }) => {
                      const budget = calculateEstimatedBudget(
                        values.startDate as Date,
                        values.endDate as Date,
                        +target.value,
                      );
                      setFieldValue("estimatedBudget", budget, false);
                      setFieldValue("dailyBudget", target.value, false);
                      maxBidCheckErrorsOnChange(values.maxBid as number, budget, setFieldError);
                      if ((target.value as number) > 0 && target.value) {
                        setFieldError("dailyBudget", undefined);
                      }
                      dispatch(setCampaignBasicDetails({ dailyBudget: +target.value, estimatedBudget: budget }));
                    }}
                  />
                </InputSkin>
                <Box style={{ minHeight: 15 }}>
                  <FormFieldError name="dailyBudget" />
                </Box>
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>Estimated Budget</FieldLabel>
                <FieldRow>
                  <InputSkin>
                    <Field
                      as={CurrencyFormatInput}
                      value={values.estimatedBudget}
                      name="estimatedBudget"
                      variant="outlined"
                      disabled
                    />
                  </InputSkin>
                  <Tooltip title={tooltips.estimatedBudget}>
                    <TooltipIcon />
                  </Tooltip>
                </FieldRow>
                <Box style={{ minHeight: 15 }}>
                  <FormFieldError name="estimatedBudget" />
                </Box>
              </FieldGroup>
            </SubGrid2>
          </FormColumn>

          <button ref={ref} type="submit" aria-label="Submit" style={{ display: "none" }} />
        </Form>
      )}
    </Formik>
  );
};

export default React.forwardRef(BudgetAndTimelinePanelForm);
